import { FC, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { StoreApi, UseBoundStore } from 'zustand'

import { OverlayControlContext } from '@/components/ui/overlay/overlay-control.context'

import { PopupProviderStoreType } from './create-popup-factory.types'

export type PopupProviderProps = {
  usePopupStore: UseBoundStore<StoreApi<PopupProviderStoreType>>
  mountProvider: () => () => void
  startClosing: () => void
  clear: (wholeStack?: boolean) => void
}

export const PopupProvider: FC<PopupProviderProps> = ({
  usePopupStore,
  mountProvider,
  startClosing,
  clear,
}) => {
  const { stack, isClosing } = usePopupStore()
  const location = useLocation()
  const isFirstRender = useRef(true)

  useEffect(mountProvider, [mountProvider])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    clear(true)
  }, [location.pathname])

  if (stack.length === 0) return null

  return (
    <>
      {stack.map((entry, index) => {
        const isActive = index === stack.length - 1
        const { id, component: ActivePopup, props: popupProps } = entry

        // Note: onClose triggers the close animation via context.
        // The actual unmounting (clear) happens in onAfterClose once the animation completes.
        const handleClosePopup = () => {
          popupProps?.onClose?.()
          startClosing()

          return new Promise<void>((resolve) => {
            usePopupStore.setState((state) => ({
              resolveClose: (state.resolveClose ?? []).concat(resolve),
            }))
          })
        }

        return (
          // Note: Stable id key ensures React keeps the same component instance when a popup
          // transitions between active and suspended states, preserving all internal state.
          <OverlayControlContext.Provider
            key={id}
            value={{
              hasSuspendedBelow: isActive && stack.length > 1,
              isClosing: isActive ? isClosing : false,
              isSuspended: !isActive,
              onAfterClose: isActive
                ? () => {
                    popupProps?.onClose?.()
                    clear()
                  }
                : undefined,
              registerAnimated: isActive
                ? () => usePopupStore.setState({ isAnimated: true })
                : undefined,
            }}
          >
            <ActivePopup
              {...(popupProps || {})}
              onClose={isActive ? handleClosePopup : () => Promise.resolve()}
            />
          </OverlayControlContext.Provider>
        )
      })}
    </>
  )
}
